import React, { useMemo, useState } from 'react';
import { CloseIcon as XMarkIcon, PlusIcon, SearchIcon, ChevronDownIcon } from './icons';
import type { MasterCrudField, AdvancedFilter } from './MasterCrud.types';

interface MasterCrudAdvancedSearchProps {
  fields: MasterCrudField[];
  onSearch: (filters: AdvancedFilter[]) => void;
  onClose: () => void;
  initialSearchTerm?: string;
  initialSearchField?: string;
  /**
   * @deprecated El conector lógico global (Y/O) se muestra siempre en el panel de
   * filtros avanzados; esta prop ya no controla su visibilidad.
   */
  showConnectors?: boolean;
  maxFilters?: number;
  /** Lista blanca de columnas filtrables (mismas que muestran el embudo en la tabla). */
  filterableFields?: string[];
  columnFilterableFields?: string[];
  t: (key: string, defaultValue?: string) => string;
}

// Operadores del selector "Condición" del prototipo → operador del filtro server-side.
const OPERATORS = [
  { value: 'equals', labelKey: 'opEquals', label: 'Es igual a' },
  { value: 'contains', labelKey: 'opContains', label: 'Contiene' },
  { value: 'startsWith', labelKey: 'opStartsWith', label: 'Empieza con' },
  { value: 'gt', labelKey: 'opGt', label: 'Es mayor que' },
  { value: 'lt', labelKey: 'opLt', label: 'Es menor que' },
] as const;

interface ConditionRow { id: string; field: string; operator: string; value: string; }

let rowSeq = 0;
const newRow = (field = ''): ConditionRow => ({ id: `cond-${++rowSeq}`, field, operator: 'contains', value: '' });

/**
 * MasterCrudAdvancedSearch — Panel de filtros (prototipo `panel-filtros`).
 *
 * - **Filtros básicos**: un filtro por cada columna marcada como filtrable
 *   (`columnFilterableFields`). Texto = input (contains); enum/boolean = select (equals).
 * - **Filtros avanzados** (`<details>`): condiciones combinadas (Columna/Condición/Valor).
 * - Ambos son **mutuamente excluyentes**: al abrir "Filtros avanzados" se deshabilitan los
 *   básicos y, al buscar, se envían solo las condiciones avanzadas; si está cerrado, se envían
 *   los filtros básicos. Todo viaja server-side por el canal `advancedFilters`.
 */
export const MasterCrudAdvancedSearch: React.FC<MasterCrudAdvancedSearchProps> = ({
  fields,
  onSearch,
  onClose,
  filterableFields = [],
  columnFilterableFields,
  t,
}) => {
  const whitelist = columnFilterableFields ?? (filterableFields.length ? filterableFields : undefined);
  const filterableCols = useMemo(
    () => fields.filter((f) =>
      f.type !== 'lookup' &&
      (whitelist ? whitelist.includes(f.accessorKey as string) : f.config?.searchable !== false)
    ),
    [fields, whitelist]
  );

  const enumOptionsFor = (field: MasterCrudField): { value: string; label: string }[] | null => {
    if (field.type === 'boolean') {
      return [
        { value: 'true', label: field.config?.trueLabel || t('active', 'Activo') },
        { value: 'false', label: field.config?.falseLabel || t('inactive', 'Inactivo') },
      ];
    }
    if (field.type === 'select' && field.options?.length) {
      return field.options.map((o) => ({ value: String(o.value), label: o.label }));
    }
    return null;
  };

  const [basic, setBasic] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [rows, setRows] = useState<ConditionRow[]>([newRow()]);
  // Conector lógico global que une TODAS las condiciones avanzadas (sin agrupación).
  // 'AND' (Y) por defecto: se deben cumplir todas; 'OR' (O): basta con una.
  const [connector, setConnector] = useState<'AND' | 'OR'>('AND');

  const setBasicValue = (key: string, value: string) =>
    setBasic((prev) => { const next = { ...prev }; if (value === '') delete next[key]; else next[key] = value; return next; });

  const updateRow = (id: string, patch: Partial<ConditionRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  const addRow = () => setRows((prev) => [...prev, newRow()]);

  // Construye los AdvancedFilter según el modo activo (mutuamente excluyente).
  const buildFilters = (): AdvancedFilter[] => {
    if (advancedOpen) {
      const leaves: AdvancedFilter[] = rows
        .filter((r) => r.field && String(r.value).trim() !== '')
        .map((r) => ({ id: r.id, field: r.field, connector, operator: r.operator, value: r.value }));

      // Con conector 'O' se envuelven en un único grupo OR para que los consumidores
      // (y el adaptador MasterPattern) los evalúen como "al menos una condición".
      // Con 'Y' se envían planos: el nivel raíz es un AND implícito.
      if (connector === 'OR' && leaves.length > 1) {
        return [{ id: 'adv-or-group', isGroup: true, connector: 'OR', children: leaves }];
      }
      return leaves;
    }
    return Object.entries(basic).map(([field, value]) => {
      const f = filterableCols.find((c) => (c.accessorKey as string) === field);
      const isEnum = !!(f && enumOptionsFor(f));
      return { id: `basic-${field}`, field, connector: 'AND' as const, operator: isEnum ? 'equals' : 'contains', value };
    });
  };

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSearch(buildFilters()); };
  const handleClear = () => { setBasic({}); setRows([newRow()]); onSearch([]); };

  const controlCls = 'w-full px-3 py-2 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors';
  const condCtrlCls = 'px-2.5 py-1.5 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary focus:outline-none focus:border-[#93d1fd] transition-colors';

  return (
    <section aria-label={t('filters', 'Filtros')} className="bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-sm p-5 flex-shrink-0">
      <form className="space-y-5" onSubmit={handleSubmit} onReset={(e) => { e.preventDefault(); handleClear(); }}>
        {/* ── Filtros básicos (uno por columna filtrable) ── */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 transition-opacity duration-200 ${advancedOpen ? 'opacity-50 pointer-events-none' : ''}`} {...(advancedOpen ? { inert: '' as any } : {})}>
          {filterableCols.map((field) => {
            const key = field.accessorKey as string;
            const enumOptions = enumOptionsFor(field);
            const label = t(key, field.header);
            return (
              <div key={key}>
                <label className="block text-[11px] font-semibold text-content-tertiary dark:text-dark-content-tertiary tracking-[0.3px] mb-1">{label}</label>
                {enumOptions ? (
                  <select className={`${controlCls} cursor-pointer`} value={basic[key] ?? ''} onChange={(e) => setBasicValue(key, e.target.value)}>
                    <option value="">{t('all', 'Todos')}</option>
                    {enumOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input type="text" autoComplete="off" className={controlCls} value={basic[key] ?? ''} onChange={(e) => setBasicValue(key, e.target.value)} />
                )}
              </div>
            );
          })}
        </div>

        {/* ── Filtros avanzados (colapsable) ── */}
        <details className="border border-border-primary dark:border-dark-border-primary rounded-lg overflow-hidden" open={advancedOpen} onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}>
          <summary className="flex items-center gap-2 cursor-pointer select-none px-4 py-3 bg-white dark:bg-dark-bg-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors" style={{ listStyle: 'none' }}>
            <ChevronDownIcon className={`w-4 h-4 text-primary-600 flex-shrink-0 transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`} />
            <span className="text-sm font-semibold text-[#1355b7] dark:text-primary-400">{t('advancedFilters', 'Filtros avanzados')}</span>
            <span className="hidden sm:inline text-xs text-content-tertiary font-normal">{t('advancedFiltersHint', '— combina varias condiciones personalizadas')}</span>
          </summary>

          <div className="border-t border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-secondary px-4 py-4 flex flex-col gap-4">
            {/* ── Conector lógico global (Y / O) que une todas las condiciones ── */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-[11px] font-semibold text-content-secondary tracking-[0.3px] uppercase">
                {t('joinConditionsWith', 'Unir condiciones con')}
              </span>
              <div role="radiogroup" aria-label={t('joinConditionsWith', 'Unir condiciones con')} className="inline-flex rounded-md border border-border-primary dark:border-dark-border-primary overflow-hidden bg-white dark:bg-dark-bg-primary shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]">
                {([
                  { v: 'AND' as const, label: t('connectorAnd', 'Y') },
                  { v: 'OR' as const, label: t('connectorOr', 'O') },
                ]).map((opt, i) => {
                  const active = connector === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setConnector(opt.v)}
                      className={`px-4 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${i === 1 ? 'border-l border-border-primary dark:border-dark-border-primary' : ''} ${active ? 'bg-primary-600 text-white' : 'bg-white dark:bg-dark-bg-primary text-content-secondary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <span className="text-xs text-content-tertiary">
                {connector === 'AND'
                  ? t('connectorAndHint', 'Se deben cumplir todas las condiciones')
                  : t('connectorOrHint', 'Basta con que se cumpla una condición')}
              </span>
            </div>

            {/* Tarjeta de condiciones (blanca, flota) */}
            <div className="rounded-lg bg-white dark:bg-dark-bg-primary overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] ring-1 ring-black/[0.04]">
              {/* Encabezado */}
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white dark:bg-dark-bg-primary border-b border-border-primary dark:border-dark-border-primary text-[11px] font-semibold text-content-secondary tracking-[0.3px] uppercase">
                <span className="w-5 flex-shrink-0 text-center">#</span>
                <span className="w-[200px]">{t('column', 'Columna')}</span>
                <span className="w-[180px]">{t('condition', 'Condición')}</span>
                <span className="flex-1 min-w-0">{t('value', 'Valor')}</span>
                <span className="w-8 flex-shrink-0" />
              </div>

              {rows.map((row, idx) => (
                <React.Fragment key={row.id}>
                <div className="flex flex-col md:flex-row md:items-center gap-2 px-3 py-2.5">
                  <span className="w-5 flex-shrink-0 text-center text-xs font-bold text-content-tertiary select-none">{idx + 1}</span>
                  <select aria-label={t('column', 'Columna')} className={`${condCtrlCls} w-full md:w-[200px] cursor-pointer`} value={row.field} onChange={(e) => updateRow(row.id, { field: e.target.value })}>
                    <option value="">{t('selectColumn', 'Selecciona una columna…')}</option>
                    {filterableCols.map((f) => <option key={f.accessorKey as string} value={f.accessorKey as string}>{t(f.accessorKey as string, f.header)}</option>)}
                  </select>
                  <select aria-label={t('condition', 'Condición')} className={`${condCtrlCls} w-full md:w-[180px] cursor-pointer`} value={row.operator} onChange={(e) => updateRow(row.id, { operator: e.target.value })}>
                    {OPERATORS.map((op) => <option key={op.value} value={op.value}>{t(op.labelKey, op.label)}</option>)}
                  </select>
                  <input type="text" aria-label={t('value', 'Valor')} placeholder={t('writeValue', 'Escribe un valor…')} autoComplete="off" className={`${condCtrlCls} w-full md:flex-1 md:min-w-0`} value={row.value} onChange={(e) => updateRow(row.id, { value: e.target.value })} />
                  <button type="button" title={t('removeCondition', 'Eliminar condición')} aria-label={t('removeCondition', 'Eliminar condición')} onClick={() => removeRow(row.id)} className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 self-end md:self-auto rounded-md text-content-tertiary hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Conector entre condiciones (refleja el selector global Y/O) */}
                {idx < rows.length - 1 && (
                  <div className="flex items-center gap-2 px-3">
                    <span className="ml-5 inline-flex items-center justify-center h-5 min-w-[26px] px-2 rounded-full text-[10px] font-bold tracking-wide bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800 select-none">
                      {connector === 'AND' ? t('connectorAnd', 'Y') : t('connectorOr', 'O')}
                    </span>
                    <span className="h-px flex-1 bg-border-primary/70 dark:bg-dark-border-primary/70" />
                  </div>
                )}
                </React.Fragment>
              ))}
            </div>

            <div>
              <button type="button" onClick={addRow} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-primary-600 border border-dashed border-[#93d1fd] hover:bg-[#eff8ff] dark:hover:bg-primary-900/20 transition-colors">
                <PlusIcon className="w-3.5 h-3.5" />
                {t('addCondition', 'Agregar condición')}
              </button>
            </div>
          </div>
        </details>

        {/* ── Botones ── */}
        <div className="flex justify-end gap-2">
          <button type="reset" className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-sm font-medium whitespace-nowrap bg-transparent border border-[#93d1fd] text-primary-600 shadow-sm hover:bg-[#dbeefe] dark:hover:bg-primary-900/20 transition-all duration-150">
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" /><path d="M22 21H7" /><path d="m5 11 9 9" /></svg>
            {t('clear', 'Limpiar')}
          </button>
          <button type="submit" className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-sm font-medium whitespace-nowrap bg-primary-600 text-white shadow-inner hover:brightness-110 transition-all duration-150">
            <SearchIcon className="w-4 h-4 shrink-0" />
            {t('search', 'Buscar')}
          </button>
        </div>
      </form>
    </section>
  );
};
